/**
 * useSlashCommands Hook
 * 
 * Handles slash command functionality for chat interfaces.
 * Detects "/" input, manages autocomplete state, and processes template selection.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { promptTemplateService, PromptTemplate } from '../services/promptTemplateService';

interface UseSlashCommandsOptions {
  onTemplateSelect?: (template: PromptTemplate, displayText: string, actualPrompt: string) => void;
}

// Remove the interface since we're not using it consistently

interface SlashCommandState {
  isAutocompleteVisible: boolean;
  searchQuery: string;
  autocompletePosition: { top: number; left: number };
  cursorPosition: number;
}

// Store template mappings outside of React state to avoid timing issues
const templateMappings = new Map<string, string>();

export const useSlashCommands = (options: UseSlashCommandsOptions = {}) => {
  const [state, setState] = useState<SlashCommandState>({
    isAutocompleteVisible: false,
    searchQuery: '',
    autocompletePosition: { top: 0, left: 0 },
    cursorPosition: 0,
  });

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const slashStartPosition = useRef<number>(-1);

  /**
   * Calculate cursor position in input element
   */
  const getCursorPosition = useCallback((): number => {
    if (!inputRef.current) return 0;
    return inputRef.current.selectionStart || 0;
  }, []);

  /**
   * Get caret pixel position for autocomplete dropdown
   */
  const getCaretPixelPosition = useCallback((position: number): { top: number; left: number } => {
    if (!inputRef.current) return { top: 0, left: 0 };

    const rect = inputRef.current.getBoundingClientRect();
    
    // For now, use a simple positioning strategy
    // Position the dropdown just below the input field, aligned to the left
    const top = rect.bottom + 8; // 8px gap below input
    const left = rect.left;

    console.log('Position calculation:', { 
      inputRect: rect, 
      calculatedPosition: { top, left },
      viewport: { width: window.innerWidth, height: window.innerHeight }
    });

    return { top, left };
  }, []);

  /**
   * Handle input changes to detect slash commands
   */
  const handleInputChange = useCallback((value: string, cursorPos?: number) => {
    const currentCursorPos = cursorPos ?? getCursorPosition();
    
    // Find the last "/" before the cursor
    let slashPos = -1;
    for (let i = currentCursorPos - 1; i >= 0; i--) {
      if (value[i] === '/') {
        // Check if this "/" is at the start of the input or preceded by whitespace
        if (i === 0 || /\s/.test(value[i - 1])) {
          slashPos = i;
          break;
        }
      } else if (/\s/.test(value[i])) {
        // Stop at whitespace - no valid slash command
        break;
      }
    }

    if (slashPos >= 0) {
      // Extract search query after the "/"
      const queryStart = slashPos + 1;
      const queryEnd = currentCursorPos;
      const query = value.substring(queryStart, queryEnd);
      
      // Only show autocomplete if we're within the slash command
      if (currentCursorPos >= queryStart) {
        slashStartPosition.current = slashPos;
        
        const position = getCaretPixelPosition(currentCursorPos);
        
        setState({
          isAutocompleteVisible: true,
          searchQuery: query,
          autocompletePosition: position,
          cursorPosition: currentCursorPos,
        });
        return;
      }
    }

    // Hide autocomplete if no valid slash command
    if (state.isAutocompleteVisible) {
      setState(prev => ({ ...prev, isAutocompleteVisible: false }));
    }
  }, [getCursorPosition, getCaretPixelPosition, state.isAutocompleteVisible]);

  /**
   * Handle template selection from autocomplete
   */
  const handleTemplateSelect = useCallback(async (template: PromptTemplate, inputValue: string, setInputValue: (value: string) => void) => {
    if (!inputRef.current || slashStartPosition.current < 0) return;

    try {
      // Get the actual prompt content
      let actualPrompt = template.prompt;
      
      console.log('=== TEMPLATE CONTENT CHECK ===');
      console.log('Template ID:', template.id);
      console.log('Template title:', template.title);
      console.log('Template prompt from list:', template.prompt);
      console.log('Is Global:', template.isGlobal);
      
      // For templates without content, try to fetch it
      if (!actualPrompt) {
        try {
          console.log('Prompt is empty, fetching full template content...');
          const fullTemplate = await promptTemplateService.getTemplate(template.id);
          console.log('Fetched template:', fullTemplate);
          actualPrompt = fullTemplate.prompt || template.title;
        } catch (error) {
          console.error('Could not fetch template content:', error);
          actualPrompt = template.title; // Fallback to title
        }
      }
      console.log('Final actual prompt:', actualPrompt);
      console.log('==============================');
      
      // Always display the title in the input field
      const displayContent = template.title;
      console.log('Using template title in input field:', template.title);
      console.log('Actual prompt content:', actualPrompt.substring(0, 100) + '...');

      // Replace the slash command with the template title
      const beforeSlash = inputValue.substring(0, slashStartPosition.current);
      const afterCursor = inputValue.substring(state.cursorPosition);
      const newValue = beforeSlash + displayContent + afterCursor;
      
      setInputValue(newValue);

      // Position cursor at the end of the inserted template title
      const newCursorPos = beforeSlash.length + displayContent.length;
      
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          inputRef.current.focus();
        }
      }, 0);

      // Hide autocomplete
      setState(prev => ({ ...prev, isAutocompleteVisible: false }));

      // Increment usage count
      try {
        await promptTemplateService.incrementUsage(template.id);
      } catch (error) {
        console.warn('Failed to increment usage count:', error);
      }

      // Store in global mapping for immediate access
      templateMappings.set(displayContent, actualPrompt);
      console.log('📝 Stored template mapping:', displayContent, '->', actualPrompt.substring(0, 50) + '...');

      // Call callback if provided with both display text and actual prompt
      if (options.onTemplateSelect) {
        options.onTemplateSelect(template, displayContent, actualPrompt);
      }

    } catch (error) {
      console.error('Error selecting template:', error);
      // Hide autocomplete on error
      setState(prev => ({ ...prev, isAutocompleteVisible: false }));
    }
  }, [state.cursorPosition, options]);

  /**
   * Close autocomplete dropdown
   */
  const closeAutocomplete = useCallback(() => {
    setState(prev => ({ ...prev, isAutocompleteVisible: false }));
    slashStartPosition.current = -1;
  }, []);

  /**
   * Handle key events for slash commands
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent, inputValue: string) => {
    // If autocomplete is visible, let it handle the key events
    if (state.isAutocompleteVisible) {
      // The autocomplete component will handle Arrow keys, Enter, and Escape
      return;
    }

    // Handle other keyboard shortcuts
    if (event.metaKey || event.ctrlKey) {
      switch (event.key) {
        case 'k':
        case 'K':
          // Cmd+K / Ctrl+K to open template management (future feature)
          event.preventDefault();
          // TODO: Open template management modal
          break;
      }
    }
  }, [state.isAutocompleteVisible]);

  /**
   * Set the input ref for position calculations
   */
  const setInputRef = useCallback((ref: HTMLInputElement | HTMLTextAreaElement | null) => {
    inputRef.current = ref;
  }, []);

  /**
   * Get the actual prompt for a message (checks if it's a template title)
   */
  const getCurrentPromptForMessage = useCallback((inputValue: string): string => {
    const trimmedInput = inputValue.trim();
    const mappedPrompt = templateMappings.get(trimmedInput);
    
    if (mappedPrompt) {
      console.log('🎯 Found template mapping for:', trimmedInput);
      console.log('🎯 Returning prompt:', mappedPrompt.substring(0, 100) + '...');
      // Clear the mapping after use
      templateMappings.delete(trimmedInput);
      return mappedPrompt;
    }
    
    console.log('🎯 No template mapping found, returning input as-is');
    return trimmedInput;
  }, []);

  return {
    // State
    isAutocompleteVisible: state.isAutocompleteVisible,
    searchQuery: state.searchQuery,
    autocompletePosition: state.autocompletePosition,
    
    // Methods
    handleInputChange,
    handleTemplateSelect,
    closeAutocomplete,
    handleKeyDown,
    setInputRef,
    getCurrentPromptForMessage,
  };
};